import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import cn from '@/utils/cn';
import Modal from '@/components/common/Modal';
import LocaleToggle from '@/components/common/LocaleToggle';
import { LinkButton, Button } from '@/components/ui/Buttons';
import Text from '@/components/ui/Text';
import { queryClient } from '@/utils/ReactQuery';
import { IRubric, useQuestionRubric } from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import I18n from 'i18n-js';
import AnalyticRubric from '@/components/staffer/ExerciseBuilder/Rubric/AddRubricModal/AnalyticRubric';
import Icon from '@/components/ui/Icon';
import StandardButton from '@/components/ui/Buttons/StandardButton';
import {
  DEFAULT_LEVEL,
} from '@/components/staffer/ExerciseBuilder/Rubric/AddRubricModal/AnalyticRubric/AnalyticRubric.utils';
import {
  createRubric,
  updateRubric,
  allCriterionNamesUnique,
  getEmptyAnalyticCellCount,
  getEmptyHolisticCellCount,
} from './AddRubricModal.utils';
import useAnalyticRubricEditor, {
  createDefaultCriterion,
} from './AnalyticRubric/hooks/useAnalyticRubricEditor';
import useHolisticRubricEditor
  from './HolisticRubric/hooks/useHolisticRubricEditor';
import HolisticRubric from './HolisticRubric';
import QuestionPrompt from './QuestionPrompt';
import { IRubricModalQuestion, IRubricModalQuestionTranslation } from './AddRubricModal.types';

const INCOMPLETE_RUBRIC_ERROR = 'The rubric is incomplete. Please fill out any blank cells.';

interface AddRubricModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: IRubricModalQuestion;
  translations: [IRubricModalQuestionTranslation<'en'>, IRubricModalQuestionTranslation<'es'>];
  holisticRubricSelected?: boolean;
  analyticRubricSelected?: boolean;
}

const AddRubricModal = ({
  isOpen: isModalOpen,
  onClose,
  question,
  translations,
  holisticRubricSelected,
  analyticRubricSelected,
}: AddRubricModalProps) => {
  const [locale, setLocale] = useState<Lowercase<Locale>>('en');
  const [isOpen, setIsOpen] = useState(true);
  const [rubricError, setRubricError] = useState('');
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [validationAttempt, setValidationAttempt] = useState(0);

  const translation = translations.find(t => t.locale === locale)
    || translations.find(t => t.locale === 'en');

  const { data: rubric } = useQuestionRubric({ questionId: question.id });
  const { data: holisticData, table: holisticTable } = useHolisticRubricEditor({ locale, questionId: question.id });
  const {
    data: analyticData,
    deleteCriterion,
    setData: setAnalyticData,
    table: analyticTable,
  } = useAnalyticRubricEditor({ locale, questionId: question.id });

  const isHolistic = rubric?.rubric_type === 'holistic' || holisticRubricSelected;
  const isAnalytic = rubric?.rubric_type === 'analytic' || analyticRubricSelected;

  useEffect(() => {
    setAutoTranslate(rubric?.automatic_translation_enabled ?? true);
  }, [rubric]);

  const { mutate: createRubricMutate, isLoading: isCreating } = useMutation({
    mutationFn: () => createRubric(
      question.id,
      (isHolistic ? 'holistic' : 'analytic'),
      (isHolistic ? holisticData : analyticData),
      autoTranslate,
    ),
    onError: () => {
      setRubricError('Error submitting rubric');
    },
    onSuccess: (data: { data: IRubric }) => {
      queryClient.setQueryData(['useRubric', { for_question: question.id }], data.data);
      onClose();
    },
  });

  const { mutate: updateRubricMutate, isLoading: isUpdating } = useMutation({
    mutationFn: () => updateRubric(
      rubric?.id,
      (isHolistic ? 'holistic' : 'analytic'),
      (isHolistic ? holisticData : analyticData),
      autoTranslate,
    ),
    onError: () => {
      setRubricError('Error submitting rubric');
    },
    onSuccess: async () => {
      if (autoTranslate) await queryClient.invalidateQueries({ queryKey: ['rubric', rubric?.id] });
      if (rubric?.id) await queryClient.invalidateQueries(['useRubric', { for_question: question.id }]);
      onClose();
    },
  });

  const handleSetAutoTranslate = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => setAutoTranslate(e.target.checked);

  const handleRubricSave = () => {
    setValidationAttempt(prev => prev + 1);
    setRubricError('');

    if (isHolistic && getEmptyHolisticCellCount(holisticData) > 0) {
      setRubricError(INCOMPLETE_RUBRIC_ERROR);
      return;
    }

    if (isAnalytic) {
      if (getEmptyAnalyticCellCount(analyticData) > 0) {
        setRubricError(INCOMPLETE_RUBRIC_ERROR);
        return;
      }
      if (!allCriterionNamesUnique(analyticData)) {
        setRubricError('All criterion names should be unique.');
        return;
      }
    }

    setRubricError('');
    if (rubric?.id) { updateRubricMutate(); } else { createRubricMutate(); }
  };

  const handleAddCriteriaRow = () => {
    setAnalyticData(prev => [
      ...prev,
      createDefaultCriterion('', prev[0]?.levels?.map((level) => {
        const { score, level_id: levelId } = level;
        return DEFAULT_LEVEL({ levelId, score });
      })),
    ]);
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Modal
      allowOverflow
      nearlyFullScreen
      closeModal={onClose}
      headerText="Edit Rubric"
      isOpen={isModalOpen}
    >
      <QuestionPrompt isOpen={isOpen} setIsOpen={setIsOpen} translation={translation} />
      <div className="tw-flex tw-items-center">
        <LocaleToggle
          btnClasses="tw-py-4"
          debounce={0}
          isSaving={false}
          locale={locale}
          setLocale={setLocale}
        />
        <label className="tw-flex tw-ml-8 tw-items-center tw-cursor-pointer">
          <input checked={autoTranslate} className="tw-mr-2" type="checkbox" onChange={handleSetAutoTranslate} />
          <Text size="sm">Enable auto-translate</Text>
        </label>
        {isAnalytic && (
          <StandardButton
            outlineStyle
            className="tw-h-8 tw-flex tw-items-center tw-ml-auto"
            disabled={analyticData.length >= 10}
            onClick={handleAddCriteriaRow}
          >
            <Icon className="tw-mr-2" icon="Add" size={16} />
            <Text size="sm">{I18n.t('add_criteria')}</Text>
          </StandardButton>
        )}
      </div>
      {isHolistic && <HolisticRubric table={holisticTable} validationAttempt={validationAttempt} />}
      {isAnalytic && (
        <AnalyticRubric
          editor
          locale={locale}
          table={analyticTable}
          validationAttempt={validationAttempt}
          onDeleteCriteria={deleteCriterion}
        />
      )}
      <div className={cn(
        'tw-flex tw-justify-end tw-sticky tw-bottom-0 tw-left-0 tw-right-0',
        'tw-items-center tw-py-6 tw-bg-white tw-z-10',
      )}
      >
        {!!rubricError && (<Text className="tw-mr-4 !tw-text-red" size="sm">{rubricError}</Text>)}
        <LinkButton onClick={onClose}>{I18n.t('cancel', { locale })}</LinkButton>
        <Button disabled={isLoading} onClick={handleRubricSave}>
          {isLoading ? I18n.t('saving', { locale }) : I18n.t('save_rubric', { locale })}
        </Button>
      </div>
    </Modal>
  );
};

export default AddRubricModal;
